import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  MenuItem,
  Select,
  InputLabel,
  FormControl
} from "@mui/material";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from "axios";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Tooltip from '@mui/material/Tooltip';

const Visitors = () => {
  const [visitors, setVisitors] = useState([]);
  const [newVisitor, setNewVisitor] = useState({
    visitor_name: "",
    company: "",
    visit_reason: "",
    rfid_card_id: ""
  });
  const [editMode, setEditMode] = useState(false);
  const [editVisitor, setEditVisitor] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [rfidCards, setRfidCards] = useState([]);
  const [visitorAttendance, setVisitorAttendance] = useState([]);

  useEffect(() => {
    fetchVisitors();
    fetchRfidCards();
    fetchVisitorAttendance();
  }, []);

  const fetchVisitors = async () => {
    try {
      const res = await axios.get('https://pdks.bytebridge.com.tr/api/visitors');
      setVisitors(res.data);
    } catch (err) {
      setSnackbar({ open: true, message: 'Ziyaretçiler alınamadı', severity: 'error' });
    }
  };

  const fetchRfidCards = async () => {
    try {
      const res = await axios.get('https://pdks.bytebridge.com.tr/api/rfidcards');
      setRfidCards(res.data);
    } catch (err) {
      setSnackbar({ open: true, message: 'RFID kartlar alınamadı', severity: 'error' });
    }
  };

  const fetchVisitorAttendance = async () => {
    try {
      const visitorsRes = await axios.get('https://pdks.bytebridge.com.tr/api/visitors');
      const attendanceRes = await axios.get('https://pdks.bytebridge.com.tr/api/attendance');
      const visitors = visitorsRes.data;
      const attendance = attendanceRes.data.attendance;
      const merged = visitors.map(visitor => {
        if (!visitor.RfidCardID) return { ...visitor, FirstEntry: null, LastExit: null };
        const records = attendance.filter(a => String(a.EmployeeID) === String(visitor.RfidCardID));
        if (records.length === 0) return { ...visitor, FirstEntry: null, LastExit: null };
        const sortedByEntry = [...records].sort((a, b) => new Date(a.EntryTime) - new Date(b.EntryTime));
        const sortedByExit = [...records].sort((a, b) => new Date(b.ExitTime) - new Date(a.ExitTime));
        return {
          ...visitor,
          FirstEntry: sortedByEntry[0]?.EntryTime || null,
          LastExit: sortedByExit[0]?.ExitTime || null
        };
      });
      setVisitorAttendance(merged);
    } catch (err) {
      // Hata yönetimi
    }
  };

  const handleChange = (e) => {
    setNewVisitor({ ...newVisitor, [e.target.name]: e.target.value });
  };

  const handleAddVisitor = async () => {
    if (!newVisitor.visitor_name || !newVisitor.rfid_card_id) {
      setSnackbar({ open: true, message: 'Zorunlu alanları doldurun', severity: 'warning' });
      return;
    }
    try {
      await axios.post('https://pdks.bytebridge.com.tr/api/visitors', newVisitor);
      setSnackbar({ open: true, message: 'Ziyaretçi eklendi', severity: 'success' });
      setNewVisitor({ visitor_name: "", company: "", visit_reason: "", rfid_card_id: "" });
      await refreshVisitorsAndAttendance();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error || 'Ziyaretçi eklenemedi',
        severity: 'error'
      });
    }
  };

  const handleEditClick = (visitor) => {
    setEditMode(true);
    setEditVisitor(visitor);
    setNewVisitor({
      visitor_name: visitor.VisitorName,
      company: visitor.Company,
      visit_reason: visitor.VisitReason,
      rfid_card_id: visitor.RfidCardID || ""
    });
  };

  const handleUpdateVisitor = async () => {
    if (!editVisitor) return;
    try {
      await axios.put(`https://pdks.bytebridge.com.tr/api/visitors/${editVisitor.ID}`, newVisitor);
      setSnackbar({ open: true, message: 'Ziyaretçi güncellendi', severity: 'success' });
      setEditMode(false);
      setEditVisitor(null);
      setNewVisitor({ visitor_name: "", company: "", visit_reason: "", rfid_card_id: "" });
      await refreshVisitorsAndAttendance();
    } catch (err) {
      setSnackbar({ open: true, message: 'Ziyaretçi güncellenemedi', severity: 'error' });
    }
  };

  const handleDeleteClick = (visitorId) => {
    setDeleteTargetId(visitorId);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTargetId) return;
    try {
      await axios.delete(`https://pdks.bytebridge.com.tr/api/visitors/${deleteTargetId}`);
      setSnackbar({ open: true, message: 'Ziyaretçi silindi', severity: 'success' });
      await refreshVisitorsAndAttendance();
    } catch (err) {
      setSnackbar({ open: true, message: 'Ziyaretçi silinemedi', severity: 'error' });
    } finally {
      setDeleteDialogOpen(false);
      setDeleteTargetId(null);
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setDeleteTargetId(null);
  };

  const handleSnackbarClose = () => setSnackbar({ ...snackbar, open: false });

  // --- Tabloları güncelleyen yardımcı fonksiyon ---
  const refreshVisitorsAndAttendance = async () => {
    await fetchVisitors();
    await fetchVisitorAttendance();
  };

  // --- Kullanılmış kartları filtrele ---
  const usedRfidCardIDs = visitorAttendance.map(v => v.RfidCardID).filter(Boolean);
  const availableRfidCards = rfidCards.filter(card => {
    const cardId = card.CardID || card.card_id || card.id;
    // Eğer edit modundaysak ve mevcut ziyaretçinin kartı ise gösterilsin
    if (editMode && editVisitor && String(editVisitor.RfidCardID) === String(cardId)) return true;
    return !usedRfidCardIDs.includes(cardId);
  });

  return (
    <Box p={3}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
        <Tooltip title="Ziyaretçi işlemlerini bu sayfadan yönetebilirsiniz.">
          <InfoOutlinedIcon color="primary" style={{ marginRight: 8, cursor: 'pointer' }} />
        </Tooltip>
        <Typography variant="h5" gutterBottom>
          Ziyaretçiler
        </Typography>
      </div>
      <Box display="flex" gap={2} mb={2}>
        <TextField
          label="Ziyaretçi Adı"
          name="visitor_name"
          value={newVisitor.visitor_name}
          onChange={handleChange}
        />
        <TextField
          label="Şirket"
          name="company"
          value={newVisitor.company}
          onChange={handleChange}
        />
        <TextField
          label="Ziyaret Nedeni"
          name="visit_reason"
          value={newVisitor.visit_reason}
          onChange={handleChange}
        />
        <FormControl style={{ minWidth: 150 }}>
          <InputLabel>RFID Kart ID</InputLabel>
          <Select
            label="RFID Kart ID"
            name="rfid_card_id"
            value={newVisitor.rfid_card_id}
            onChange={handleChange}
          >
            <MenuItem value=""><em>Seçiniz</em></MenuItem>
            {availableRfidCards.map(card => (
              <MenuItem key={card.CardID || card.card_id || card.id} value={card.CardID || card.card_id || card.id}>
                {card.CardID || card.card_id || card.id}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button variant="contained" onClick={editMode ? handleUpdateVisitor : handleAddVisitor} color={editMode ? 'warning' : 'primary'}>
          {editMode ? 'Güncelle' : 'Ekle'}
        </Button>
        {editMode && (
          <Button variant="outlined" color="secondary" onClick={() => { setEditMode(false); setEditVisitor(null); setNewVisitor({ visitor_name: '', company: '', visit_reason: '', rfid_card_id: '' }); }}>
            İptal
          </Button>
        )}
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Ziyaretçi Adı</TableCell>
              <TableCell>Şirket</TableCell>
              <TableCell>Ziyaret Nedeni</TableCell>
              <TableCell>RFID Kart ID</TableCell>
              <TableCell>İlk Giriş</TableCell>
              <TableCell>Son Çıkış</TableCell>
              <TableCell align="right">İşlem</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visitorAttendance.map((visitor) => (
              <TableRow key={visitor.ID}>
                <TableCell>{visitor.ID}</TableCell>
                <TableCell>{visitor.VisitorName}</TableCell>
                <TableCell>{visitor.Company}</TableCell>
                <TableCell>{visitor.VisitReason}</TableCell>
                
                <TableCell>{visitor.RfidCardID}</TableCell>
                <TableCell>{visitor.FirstEntry ? new Date(visitor.FirstEntry).toLocaleString('tr-TR', { timeZone: 'UTC' }) : '-'}</TableCell>
                <TableCell>{visitor.LastExit ? new Date(visitor.LastExit).toLocaleString('tr-TR', { timeZone: 'UTC' }) : '-'}</TableCell>
                <TableCell align="right">
                  <IconButton color="primary" onClick={() => handleEditClick(visitor)}><EditIcon fontSize="small" /></IconButton>
                  <IconButton color="error" onClick={() => handleDeleteClick(visitor.ID)}><DeleteIcon fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={handleSnackbarClose}>
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      <Dialog open={deleteDialogOpen} onClose={handleCancelDelete}>
        <DialogTitle>Ziyaretçi Sil</DialogTitle>
        <DialogContent>
          <DialogContentText>Bu ziyaretçiyi silmek istediğinize emin misiniz?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>İptal</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">Sil</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Visitors;
